import { nextTick } from 'vue'
import { createWebHistory, createRouter } from 'vue-router'
import store from '@/store'
import { SET_REFERRAL_EMAIL } from '@/store/mutation-types'
import debug from '@/helpers/debug'
import { scrollWindowToTop } from '@/helpers/scrollWindowToTop'
import {
  requiresUserToBeUnauthenticated,
  requiresUserToBeAuthenticated,
  handleSignUpState,
  setRequestLoadingFalse,
  redirectOnAuthCallbackError,
  storeRequestedPage,
  clearReachedCallbackLandingPage,
  catchAuth0CallbackQueryParam,
  checkLoginMethod,
  setAuthIntentFromRoute,
  debugRouterGuard,
} from '@/router/guards'

import AccountVerify from '@/views/account-verify.vue'
import AuthCallback from '@/views/manage-account/callback.vue'
import MaintenancePage from '@/views/maintenance.vue'
import NotFoundPage from '@/views/not-found.vue'

// ManageAccount
import ManageAccount from '@/views/manage-account/index.vue'
import ManageAccountLogin from '@/views/manage-account/login.vue'
import ManageAccountCloseRouterView from '@/views/manage-account/close/router-view.vue'
import ManageAccountClose from '@/views/manage-account/close/index.vue'
import ManageAccountCloseConfirm from '@/views/manage-account/close/confirm.vue'
import ManageAccountUserNotFound from '@/views/manage-account/user-not-found.vue'
import ManageAccountCreateRouterView from '@/views/manage-account/create/router-view.vue'
import ManageAccountCreateDetails from '@/views/manage-account/create/details.vue'
import ManageAccountCreateDeclaration from '@/views/manage-account/create/declaration.vue'

// Dashboard
import DashboardRouterView from '@/views/dashboard/router-view.vue'
import DashboardHome from '@/views/dashboard/home.vue'
import DashboardDonations from '@/views/dashboard/donations.vue'
import DashboardClaimTaxBack from '@/views/dashboard/claim-tax-back.vue'
import DashboardStatements from '@/views/dashboard/statements.vue'
import DashboardAccount from '@/views/dashboard/account.vue'
import DashboardSettingsRenewAuthorisation from '@/views/dashboard/settings/renew-authorisation.vue'
import DashboardSupport from '@/views/dashboard/support.vue'

const authEntranceGuards = [
  setAuthIntentFromRoute,
  // Email CTAs pass ?callbacklandingpage=ClaimTaxBack on /login. Store it
  // before the signed-in redirect and before Auth0, so login opens that page.
  catchAuth0CallbackQueryParam,
  checkLoginMethod,
  requiresUserToBeUnauthenticated,
  setRequestLoadingFalse,
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      name: 'Root',
      path: '/',
      redirect: { name: 'Login' },
    },
    {
      name: 'Maintenance',
      path: '/maintenance',
      component: MaintenancePage,
      meta: {
        title: 'Maintenance | Swiftaid',
        layout: 'AuthLayout',
      },
    },
    {
      name: 'Login',
      path: '/login',
      component: ManageAccountLogin,
      meta: {
        layout: 'AuthLayout',
        manageAccount: true,
        title: 'Login | Swiftaid',
        authIntent: 'login',
        loginMethod: 'code',
      },
      beforeEnter: authEntranceGuards,
    },
    {
      name: 'LoginMagicLink',
      path: '/login/magic-link',
      component: ManageAccountLogin,
      meta: {
        layout: 'AuthLayout',
        manageAccount: true,
        title: 'Magic link login | Swiftaid',
        authIntent: 'login',
        loginMethod: 'link',
      },
      beforeEnter: authEntranceGuards,
    },
    {
      name: 'Signup',
      path: '/signup',
      component: ManageAccountLogin,
      meta: {
        layout: 'AuthLayout',
        manageAccount: true,
        title: 'Sign up | Swiftaid',
        authIntent: 'signup',
        loginMethod: 'code',
      },
      beforeEnter: authEntranceGuards,
    },
    {
      name: 'SignupMagicLink',
      path: '/signup/magic-link',
      component: ManageAccountLogin,
      meta: {
        layout: 'AuthLayout',
        manageAccount: true,
        title: 'Magic link sign up | Swiftaid',
        authIntent: 'signup',
        loginMethod: 'link',
      },
      beforeEnter: authEntranceGuards,
    },
    {
      path: '/register',
      redirect: { name: 'Signup' },
    },
    {
      // Support-friendly alias; same as /login/magic-link
      path: '/magic-link',
      redirect: { name: 'LoginMagicLink' },
    },
    {
      path: '/manage-account',
      component: ManageAccount,
      meta: {
        layout: 'AuthLayout',
        title: 'Manage Account',
      },
      beforeEnter: [setRequestLoadingFalse],
      children: [
        {
          path: '/manage-account/create',
          component: ManageAccountCreateRouterView,
          meta: {
            layout: 'AuthLayout',
            manageAccount: true,
            authPanelWide: true,
          },
          children: [
            {
              name: 'DONOR_FLOW_ManageAccountCreateDetails',
              path: '/manage-account/create/details',
              component: ManageAccountCreateDetails,
              meta: {
                manageAccount: true,
                title: 'Registration details',
                layout: 'AuthLayout',
                authPanelWide: true,
              },
              beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
            },
            {
              name: 'DONOR_FLOW_ManageAccountCreateDeclaration',
              path: '/manage-account/create/declaration',
              component: ManageAccountCreateDeclaration,
              meta: {
                manageAccount: true,
                title: 'Registration declaration',
                layout: 'AuthLayout',
                authPanelWide: true,
              },
              beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
            },
          ],
        },
        {
          path: '/manage-account/close',
          component: ManageAccountCloseRouterView,
          meta: {
            layout: 'AuthLayout',
            manageAccount: true,
          },
          beforeEnter: [storeRequestedPage, requiresUserToBeAuthenticated],
          children: [
            {
              name: 'DONOR_FLOW_ManageAccountClose',
              path: '',
              component: ManageAccountClose,
              meta: {
                layout: 'AuthLayout',
                manageAccount: true,
                title: 'Close | Manage account',
              },
              beforeEnter: [requiresUserToBeAuthenticated],
            },
            {
              name: 'DONOR_FLOW_ManageAccountCloseConfirm',
              path: '/manage-account/close/confirm',
              component: ManageAccountCloseConfirm,
              meta: {
                layout: 'AuthLayout',
                manageAccount: true,
                title: 'Confirm close | Manage account',
              },
              beforeEnter: [requiresUserToBeAuthenticated],
            },
          ],
        },
        {
          name: 'DONOR_FLOW_ManageAccountUserNotFound',
          path: '/manage-account/user-not-found',
          component: ManageAccountUserNotFound,
          meta: {
            layout: 'AuthLayout',
            manageAccount: true,
            title: 'User not found | Manage account',
          },
          beforeEnter: [setRequestLoadingFalse],
        },
      ],
    },
    {
      path: '/dashboard',
      redirect: { name: 'Dashboard' },
      component: DashboardRouterView,
      meta: {
        default: true,
        iconClass: 'fas fa-fw fa-hand-holding-heart',
        title: 'Dashboard',
        layout: 'DashboardLayout',
      },
      beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
      children: [
        {
          name: 'Dashboard',
          path: '',
          component: DashboardHome,
          meta: {
            title: 'Dashboard | Swiftaid',
            layout: 'DashboardLayout',
            dashboardNav: 'dashboard',
          },
          beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
        },
        {
          name: 'Donations',
          path: '/dashboard/donations',
          component: DashboardDonations,
          meta: {
            title: 'Donations | Dashboard',
            layout: 'DashboardLayout',
            dashboardNav: 'donations',
          },
          beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
        },
        {
          name: 'ClaimTaxBack',
          path: '/dashboard/my-tax-relief',
          component: DashboardClaimTaxBack,
          meta: {
            title: 'My tax relief | Dashboard',
            layout: 'DashboardLayout',
            dashboardNav: 'claim-tax-back',
          },
          beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
        },
        {
          name: 'Statements',
          path: '/dashboard/statements',
          component: DashboardStatements,
          meta: {
            title: 'Statements | Dashboard',
            layout: 'DashboardLayout',
            dashboardNav: 'statements',
          },
          beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
        },
        {
          name: 'Account',
          path: '/dashboard/account',
          component: DashboardAccount,
          meta: {
            title: 'Account | Dashboard',
            layout: 'DashboardLayout',
            dashboardNav: 'account',
          },
          beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
        },
        {
          name: 'Support',
          path: '/dashboard/support',
          component: DashboardSupport,
          meta: {
            title: 'Support | Dashboard',
            layout: 'DashboardLayout',
            dashboardNav: 'support',
          },
          beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
        },
        {
          name: 'Renew',
          path: '/dashboard/account/renew-authorisation',
          component: DashboardSettingsRenewAuthorisation,
          meta: {
            title: 'Renew authorisation | Dashboard',
            layout: 'DashboardLayout',
            dashboardNav: 'account',
          },
          beforeEnter: [requiresUserToBeAuthenticated, handleSignUpState],
        },
      ],
    },
    {
      // Old claim page URL; keep bookmarks and shared links working.
      path: '/dashboard/claim-tax-back',
      redirect: (to) => ({
        name: 'ClaimTaxBack',
        query: to.query,
        hash: to.hash,
      }),
    },
    {
      name: 'AccountVerify',
      path: '/account-verify',
      component: AccountVerify,
      meta: {
        title: 'Account verification',
        layout: 'AuthLayout',
      },
      beforeEnter: [setRequestLoadingFalse],
    },
    {
      name: 'Callback',
      path: '/callback',
      component: AuthCallback,
      meta: {
        title: 'Signing in',
        layout: 'AuthLayout',
      },
      beforeEnter: [
        debugRouterGuard,
        catchAuth0CallbackQueryParam,
        requiresUserToBeUnauthenticated,
        redirectOnAuthCallbackError,
        handleSignUpState,
      ],
    },
    {
      // Unknown paths used to fall through to BlankLayout with an empty
      // router-view. Keep this last so it never shadows a real route.
      name: 'NotFound',
      path: '/:pathMatch(.*)*',
      component: NotFoundPage,
      meta: {
        title: 'Page not found',
        layout: 'AuthLayout',
      },
      beforeEnter: [setRequestLoadingFalse],
    },
  ],
  // Always land at the top after a route change. Await nextTick so layout
  // swaps (e.g. Agree → dashboard) paint before we scroll the window.
  scrollBehavior: () =>
    nextTick().then(() => {
      scrollWindowToTop()
      return { left: 0, top: 0, behavior: 'auto' as const }
    }),
})

router.beforeEach(async (to, from) => {
  debug(
    'Soft navigated to',
    `${to.fullPath} called: ${String(to.name)} on ${Date.now()}`
  )

  // Send user to maintenance page if maintenance mode is on
  if (import.meta.env.VITE_MAINTENANCE || store.state.maintenance) {
    if (to.name !== 'Maintenance') {
      return { name: 'Maintenance' }
    }

    return true
  }

  // Store referrer email query param on any path (...co.uk?email=test@example.com).
  try {
    const { email } = to.query || from.query
    if (email) {
      store.commit(SET_REFERRAL_EMAIL, email)
    }
  } catch (error) {
    debug(error)
  }

  // Mock API: ensure token is set; never block navigation on profile/plan fetches.
  // Skip profile sync on login/signup entrance screens — a delayed GET /me can
  // return the active persona (landingPage 100) and flip authIntent back to
  // login, which hides signup-only UI (e.g. the UK taxpayer checkbox).
  if (import.meta.env.MODE === 'mockapi') {
    store.dispatch('storeMockToken')
    const authEntrance =
      to.name === 'Login' ||
      to.name === 'LoginMagicLink' ||
      to.name === 'Signup' ||
      to.name === 'SignupMagicLink' ||
      to.name === 'Callback' ||
      to.name === 'DONOR_FLOW_ManageAccountUserNotFound'
    if (!authEntrance) {
      void store.dispatch('fetchUser')
      void store.dispatch('fetchDonorPlan')
    }
    return true
  }

  return true
})

// A stored landing page is used once. Clear it when navigation reaches it.
router.afterEach(clearReachedCallbackLandingPage)

const DEFAULT_TITLE = 'Swiftaid for donors'
router.afterEach((to) => {
  nextTick(() => {
    let title = DEFAULT_TITLE

    if (to.meta.title) {
      title = `${to.meta.title} | ${DEFAULT_TITLE}`
    }

    document.title = title
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute('content', title)
    document
      .querySelector('meta[name="twitter:title"]')
      ?.setAttribute('content', title)

    if (to.meta.description) {
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute('content', String(to.meta.description))
      document
        .querySelector('meta[property="og:description"]')
        ?.setAttribute('content', String(to.meta.description))
      document
        .querySelector('meta[name="twitter:description"]')
        ?.setAttribute('content', String(to.meta.description))
    }
  })
})

export default router
